import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import serviceService from '../services/service.service';
import toast from 'react-hot-toast';

// Async thunks
export const getAllServices = createAsyncThunk(
  'service/getAllServices',
  async ({ page = 1, limit = 10, filters = {} }, { rejectWithValue }) => {
    try {
      const response = await serviceService.getAllServices(page, limit, filters);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to fetch services';
      return rejectWithValue(message);
    }
  }
);

export const getPopularServices = createAsyncThunk(
  'service/getPopularServices',
  async (limit = 8, { rejectWithValue }) => {
    try {
      const response = await serviceService.getPopularServices(limit);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to fetch popular services';
      return rejectWithValue(message);
    }
  }
);

export const searchServices = createAsyncThunk(
  'service/searchServices',
  async ({ query, page = 1, limit = 10 }, { rejectWithValue }) => {
    try {
      const response = await serviceService.searchServices(query, page, limit);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to search services';
      return rejectWithValue(message);
    }
  }
);

export const getServiceById = createAsyncThunk(
  'service/getServiceById',
  async (serviceIdOrObject, { rejectWithValue }) => {
    try {
      // Support both old format (just serviceId string) and new format (object with serviceId and bypassCache)
      let id, shouldBypassCache;
      if (typeof serviceIdOrObject === 'string') {
        // Old format: just the serviceId string
        id = serviceIdOrObject;
        shouldBypassCache = false;
      } else if (typeof serviceIdOrObject === 'object' && serviceIdOrObject.serviceId) {
        // New format: object with serviceId and optional bypassCache
        id = serviceIdOrObject.serviceId;
        shouldBypassCache = serviceIdOrObject.bypassCache || false;
      } else {
        throw new Error('Invalid serviceId parameter');
      }
      const response = await serviceService.getServiceById(id, shouldBypassCache);
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to fetch service';
      return rejectWithValue(message);
    }
  }
);

export const createService = createAsyncThunk(
  'service/createService',
  async (serviceData, { rejectWithValue }) => {
    try {
      const response = await serviceService.createService(serviceData);
      toast.success('Service created successfully');
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to create service';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const updateService = createAsyncThunk(
  'service/updateService',
  async ({ serviceId, serviceData }, { rejectWithValue }) => {
    try {
      const response = await serviceService.updateService(serviceId, serviceData);
      toast.success('Service updated successfully');
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to update service';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const deleteService = createAsyncThunk(
  'service/deleteService',
  async (serviceId, { rejectWithValue }) => {
    try {
      await serviceService.deleteService(serviceId);
      toast.success('Service deleted successfully');
      return serviceId;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to delete service';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

export const getServiceStats = createAsyncThunk(
  'service/getServiceStats',
  async (_, { rejectWithValue }) => {
    try {
      const response = await serviceService.getServiceStats();
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to fetch service stats';
      return rejectWithValue(message);
    }
  }
);

export const initializeDefaultServices = createAsyncThunk(
  'service/initializeDefaultServices',
  async (_, { rejectWithValue }) => {
    try {
      const response = await serviceService.initializeDefaultServices();
      toast.success('Default services initialized successfully');
      return response;
    } catch (error) {
      const message = error.response?.data?.message || error.message || 'Failed to initialize default services';
      toast.error(message);
      return rejectWithValue(message);
    }
  }
);

const initialState = {
  services: [],
  popularServices: [],
  currentService: null,
  searchResults: [],
  stats: null,
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0,
  },
  loading: false,
  error: null,
  filters: {
    category: '',
    isPopular: '',
    search: '',
  },
};

const serviceSlice = createSlice({
  name: 'service',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearCurrentService: (state) => {
      state.currentService = null;
    },
    clearSearchResults: (state) => {
      state.searchResults = [];
    },
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload };
    },
    clearFilters: (state) => {
      state.filters = {
        category: '',
        isPopular: '',
        search: '',
      };
    },
  },
  extraReducers: (builder) => {
    builder
      // Get all services
      .addCase(getAllServices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getAllServices.fulfilled, (state, action) => {
        state.loading = false;
        state.services = action.payload.data || [];
        state.pagination = action.payload.pagination || {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0,
        };
      })
      .addCase(getAllServices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get popular services
      .addCase(getPopularServices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getPopularServices.fulfilled, (state, action) => {
        state.loading = false;
        state.popularServices = action.payload.data?.services || [];
      })
      .addCase(getPopularServices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Search services
      .addCase(searchServices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(searchServices.fulfilled, (state, action) => {
        state.loading = false;
        state.searchResults = action.payload.services || [];
        state.pagination = action.payload.pagination || {
          page: 1,
          limit: 10,
          total: 0,
          totalPages: 0,
        };
      })
      .addCase(searchServices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get service by ID
      .addCase(getServiceById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getServiceById.fulfilled, (state, action) => {
        state.loading = false;
        state.currentService = action.payload.data?.service || action.payload.data;
      })
      .addCase(getServiceById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Create service
      .addCase(createService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createService.fulfilled, (state, action) => {
        state.loading = false;
        state.services.unshift(action.payload.data);
      })
      .addCase(createService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Update service
      .addCase(updateService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateService.fulfilled, (state, action) => {
        state.loading = false;
        const updatedService = action.payload?.data?.service || action.payload?.data || action.payload;
        
        const index = state.services.findIndex(service => service._id === updatedService?._id);
        if (index !== -1 && updatedService) {
          state.services[index] = updatedService;
        }
        if (state.currentService && state.currentService._id === updatedService?._id) {
          state.currentService = updatedService;
        }
      })
      .addCase(updateService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Delete service
      .addCase(deleteService.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteService.fulfilled, (state, action) => {
        state.loading = false;
        state.services = state.services.filter(service => service._id !== action.payload);
        if (state.currentService && state.currentService._id === action.payload) {
          state.currentService = null;
        }
      })
      .addCase(deleteService.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Get service stats
      .addCase(getServiceStats.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(getServiceStats.fulfilled, (state, action) => {
        state.loading = false;
        state.stats = action.payload.data.stats;
      })
      .addCase(getServiceStats.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      // Initialize default services
      .addCase(initializeDefaultServices.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(initializeDefaultServices.fulfilled, (state, action) => {
        state.loading = false;
        state.services = action.payload.data?.services || [];
      })
      .addCase(initializeDefaultServices.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      });
  },
});

export const { 
  clearError, 
  clearCurrentService, 
  clearSearchResults, 
  setFilters, 
  clearFilters 
} = serviceSlice.actions;

export default serviceSlice.reducer;
